import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getUserWorkInfo } from "../../../actions/user-work-info";
import Alert from "../../../components/alert";
import Button from "../../../components/button";
import DatafieldSection, {
  DatafieldLabel,
} from "../../../components/datafield-section";
import { UserWorkInfo } from "../../../dto/user-work-info";
import { ReactComponent as Loader } from "../../../images/loader.svg";
import dispatch from "../../../middleware";
import { ReduxState } from "../../../reducers";
import { isLoadingActive } from "../../../utils";

const USER_WORK_INFO_LABELS: DatafieldLabel[] = [
  {
    items: [
      { label: "Employer Name", key: "employer.name" },
      { label: "Employment Type", key: "employmentType" },
      { label: "Employee ID", key: "employerEmployeeId" },
      { label: "Status", key: "status" },
      { label: "Designation", key: "designation" },
      { label: "City", key: "address.city" },
    ],
  },
  {
    items: [
      { label: "State", key: "address.state" },

      { label: "PIN Code", key: "address.pinCode" },
      { label: "Employee Address", key: "employeeAddress" },
    ],
  },
];

interface UserWorkInformationProps {
  userId: string;
}

export default function UserWorkInformation({
  userId,
}: UserWorkInformationProps) {
  const storeDispatch = useDispatch();
  const loadingQueue = useSelector(
    (state: ReduxState) => state.user.loadingQueue,
  );
  const userWorkerInfo: UserWorkInfo = useSelector(
    (state: ReduxState) => state.userWorkInfo.userWorkInfo,
  );

  const [errorMessage, setErrorMessage] = useState("");

  const isLoading = isLoadingActive(loadingQueue);

  const fetchUserWorkingInfo = useCallback(
    async (userId: string) => {
      if (!userId) return;
      try {
        await dispatch(storeDispatch, getUserWorkInfo(userId));
      } catch (error) {
        setErrorMessage(
          error?.message ?? "Unable to load user working information",
        );
      }
    },
    [storeDispatch],
  );

  useEffect(() => {
    if (!userId) return;
    fetchUserWorkingInfo(userId);
  }, [fetchUserWorkingInfo, userId]);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <Loader />
      </div>
    );
  }

  if (errorMessage) {
    return (
      <Alert message={errorMessage} type="danger" canDismiss>
        <div className="d-flex flex-column align-items-start mt-4">
          <Button
            type="button"
            variant="danger-outline"
            onClick={() => fetchUserWorkingInfo(userId)}
          >
            Retry
          </Button>
        </div>
      </Alert>
    );
  }

  const data = {
    ...userWorkerInfo,
  };

  return (
    <div className="dashboard-container">
      <h3 className="user-page-title user-details-title mb-4">
        Current Employment Details
      </h3>

      <DatafieldSection labels={USER_WORK_INFO_LABELS} data={data} />
    </div>
  );
}
